/**
 * Hero Slice
 *
 * Two variants driven by body class:
 * - Default: full-bleed background video/image, content overlaid at bottom.
 * - body.hero-alt: nav not fixed, video page-width and contained with 4rem radius.
 *
 * To use alt: add class "hero-alt" to document.body (e.g. in gatsby-browser.js
 * or your layout when a Prismic field or route says so).
 */

import React from "react";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import ReactPlayer from "react-player/lazy";
import Hls from "hls.js";
import BackgroundStaticVideo from "../../components/BackgroundStaticVideo";
import * as styles from "./Hero.module.scss";
import * as altStyles from "./HeroAlt.module.scss";

// Slower playback for hero video (1 = normal, 0.5 = half speed)
const HERO_VIDEO_PLAYBACK_RATE = 0.75;

// Native video (direct MP4/Prismic): applies playback rate
const HeroDirectVideo = ({
  src,
  poster,
  playbackRate = HERO_VIDEO_PLAYBACK_RATE,
  className,
  onPlayingChange,
}) => {
  const videoRef = React.useRef(null);
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const setRate = () => {
      video.playbackRate = playbackRate;
    };
    video.addEventListener("loadedmetadata", setRate, { once: true });
    video.playbackRate = playbackRate;
    return () => video.removeEventListener("loadedmetadata", setRate);
  }, [playbackRate]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !onPlayingChange) return;
    const onPlay = () => onPlayingChange(true);
    const onPause = () => onPlayingChange(false);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
    };
  }, [onPlayingChange]);

  return (
    <video
      ref={videoRef}
      className={className}
      src={src}
      poster={poster}
      autoPlay
      loop
      muted
      playsInline
      aria-hidden
    />
  );
};

// HLS video player: uses hls.js for Chrome/Firefox, native for Safari
const HeroHlsVideo = ({
  playlistUrl,
  posterUrl,
  className,
  playbackRate = HERO_VIDEO_PLAYBACK_RATE,
}) => {
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Listen for timeupdate events and restart video if needed.
    const restartNearEnd = () => {
      if (video.duration - video.currentTime > 1) return;
      video.currentTime = 0;
      video.play().catch(() => {});
    };

    video.addEventListener("timeupdate", restartNearEnd);
    return () => {
      video.removeEventListener("timeupdate", restartNearEnd);
    };
  }, []);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !playlistUrl) return;

    video.playbackRate = playbackRate;

    // Prefer hls.js when available for more consistent loop behavior.
    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
      });
      const playWhenReady = () => {
        video.muted = true;
        video.playbackRate = playbackRate;
        video.play().catch(() => {});
      };
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        playWhenReady();
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        playWhenReady();
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          console.error("[Hero HLS]", data.type, data.details, data);
        }
      });
      hls.loadSource(playlistUrl);
      hls.attachMedia(video);
      return () => hls.destroy();
    }

    // Fallback to native HLS when hls.js is unavailable.
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = playlistUrl;
      const onCanPlay = () => {
        video.playbackRate = playbackRate;
        video.play().catch(() => {});
      };
      video.addEventListener("loadeddata", onCanPlay, { once: true });
      video.muted = true;
      video.play().catch(() => {});
      return () => {
        video.removeEventListener("loadeddata", onCanPlay);
      };
    }
  }, [playlistUrl, playbackRate]);

  return (
    <video
      ref={videoRef}
      className={className}
      poster={posterUrl}
      autoPlay
      loop
      muted
      playsInline
      aria-hidden
    />
  );
};

export const Hero = ({ slice }) => {
  const {
    heading,
    subheading,
    background_type,
    background_image,
    background_video_url,
    video_poster,
    hls_poster,
    hls_playlist_file,
    color_scheme,
    show_background_static_video,
  } = slice?.primary || {};

  const isVideoUrl =
    (background_type === "video_url" || background_type === "video") &&
    background_video_url;
  const isHls = background_type === "hls_video";
  // Use local playlist when Prismic link is set: Prismic CDN returns 403 for segment requests (segments live in static/hero/)
  const prismicPlaylistUrl = hls_playlist_file?.url;
  const isPrismicCdn =
    prismicPlaylistUrl &&
    (prismicPlaylistUrl.includes("cdn.prismic.io") ||
      prismicPlaylistUrl.includes("prismic.io"));
  const hlsPlaylistUrl = isHls
    ? isPrismicCdn
      ? "/hero/playlist.m3u8"
      : prismicPlaylistUrl || "/hero/playlist.m3u8"
    : null;

  const isDirectFile =
    isVideoUrl &&
    (/\.(mp4|webm|ogg)(\?|$)/i.test(background_video_url || "") ||
      (background_video_url || "").includes("prismic.io"));

  const hasMedia =
    (isHls && hlsPlaylistUrl) ||
    isVideoUrl ||
    background_image?.gatsbyImageData ||
    background_image?.url;

  // Variant: body.hero-alt = contained video + nav not fixed; default = full-bleed background
  const [isAlt, setIsAlt] = React.useState(false);
  React.useEffect(() => {
    const check = () =>
      setIsAlt(document.body.classList.contains("hero-alt"));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);

  const sectionClass = [styles.hero].concat(isAlt ? [altStyles.heroAlt] : []).filter(Boolean).join(" ");

  const videoWrapperClass = isAlt ? altStyles.videoWrapper : styles.videoWrapper;
  const videoElementClass = isAlt ? altStyles.videoElement : styles.videoElement;
  const videoClass = isAlt ? altStyles.video : styles.video;

  const mediaBlock = hasMedia && (
    <>
      {isHls && hlsPlaylistUrl ? (
        <div className={videoWrapperClass}>
          <HeroHlsVideo
            playlistUrl={hlsPlaylistUrl}
            posterUrl={hls_poster?.url}
            className={videoElementClass}
            playbackRate={HERO_VIDEO_PLAYBACK_RATE}
          />
        </div>
      ) : isVideoUrl ? (
        <>
          <div className={videoWrapperClass}>
            {isDirectFile ? (
              <HeroDirectVideo
                src={background_video_url}
                poster={video_poster?.url}
                playbackRate={HERO_VIDEO_PLAYBACK_RATE}
                className={videoElementClass}
              />
            ) : (
              <ReactPlayer
                url={background_video_url}
                playing
                loop
                muted
                playsinline
                playbackRate={HERO_VIDEO_PLAYBACK_RATE}
                width="100%"
                height="100%"
                className={videoClass}
                config={{
                  file: {
                    attributes: {
                      poster: video_poster?.url || "",
                    },
                  },
                }}
              />
            )}
          </div>
          {/* Doesn't seem like this is needed? */}
          {/* {video_poster?.gatsbyImageData && (
            <GatsbyImage
              image={video_poster.gatsbyImageData}
              alt=""
              className={styles.poster}
              loading="eager"
            />
          )} */}
        </>
      ) : background_image?.gatsbyImageData ? (
        <GatsbyImage
          image={background_image.gatsbyImageData}
          alt=""
          className={styles.backgroundImage}
          loading="eager"
        />
      ) : background_image?.url ? (
        <img
          src={background_image.url}
          alt=""
          className={styles.backgroundImage}
          loading="eager"
        />
      ) : null}
      <div className={isAlt ? altStyles.overlay : styles.overlay} />
    </>
  );

  return (
    <section
      className={sectionClass}
      data-color-scheme={color_scheme || "black"}
      data-background-static-video={show_background_static_video ? "true" : undefined}
    >
      {show_background_static_video && <BackgroundStaticVideo />}
      {hasMedia && (
        <div className={isAlt ? altStyles.background : styles.background}>
          {isAlt ? (
            <div className={`page-width ${altStyles.mediaOuter}`}>
              <div className={altStyles.mediaContainer}>
                {mediaBlock}
              </div>
            </div>
          ) : (
            mediaBlock
          )}
        </div>
      )}

      {/* Content */}
      <div className={`page-width ${styles.content} ${isAlt ? altStyles.content : ""}`.trim()}>
        <div className={styles.textWrapper}>
          {isFilled.richText(heading.richText) && (
            <PrismicRichText
              field={heading.richText}
              components={{
                heading1: ({ children }) => (
                  <h1 className={styles.heading}>{children}</h1>
                ),
              }}
            />
          )}
          {isFilled.richText(subheading.richText) && (
            <div className={styles.subheading}>
              <PrismicRichText field={subheading.richText} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment HomepageDataBodyHero on PrismicHomepageDataBodyHero {
    primary {
      heading {
        richText
        text
        html
      }
      subheading {
        richText
        text
        html
      }
      background_type
      background_image {
        url
        gatsbyImageData(
          width: 1920
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
      background_video_url
      video_poster {
        url
        gatsbyImageData(
          width: 1920
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
      hls_poster {
        url
      }
      hls_playlist_file {
        url
      }
      color_scheme
      show_background_static_video
    }
  }
  fragment AboutPageDataBodyHero on PrismicAboutPageDataBodyHero {
    primary {
      heading {
        richText
        text
        html
      }
      subheading {
        richText
        text
        html
      }
      background_type
      background_image {
        url
        gatsbyImageData(
          width: 1920
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
      background_video_url
      video_poster {
        url
        gatsbyImageData(
          width: 1920
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
      hls_poster {
        url
      }
      hls_playlist_file {
        url
      }
      color_scheme
      show_background_static_video
    }
  }
`;
